/**
 * Calculates average scores for questions based on evaluations.
 * @param {Array} questions - List of questions
 * @param {Array} evaluations - List of all evaluations
 * @returns {Array} Questions with appended score metrics
 */
export const calculateQuestionScores = (questions, evaluations) => {
  // Pre-process evaluations to keep only the latest one per user per question
  const latestEvaluationsMap = new Map();
  
  evaluations.forEach(ev => {
      const key = `${ev.user_id}-${ev.question_id}`;
      const existing = latestEvaluationsMap.get(key);
      
      const evDate = ev.evaluation_date || '0000-00-00';
      const existingDate = existing?.evaluation_date || '0000-00-00';
      
      // Keep if no existing found, or this one is strictly newer, or same date but higher ID
      if (!existing || evDate > existingDate || (evDate === existingDate && ev.id > existing.id)) {
          latestEvaluationsMap.set(key, ev);
      }
  });
  
  const uniqueEvaluations = Array.from(latestEvaluationsMap.values());

  return questions.map(q => {
    // Use loose equality (==) to handle potential string/number mismatches in IDs
    const relevantEvaluations = uniqueEvaluations.filter(ev => ev.question_id == q.id);
    let averageScore = 0;
    
    if (relevantEvaluations.length > 0) {
      const sum = relevantEvaluations.reduce((acc, curr) => acc + curr.evaluation_value, 0);
      averageScore = sum / relevantEvaluations.length;
    }

    // Default to 8 (low priority/high gap) if no evaluations, 
    // consistent with IntelligentHandbook logic where 10 - 8 = 2 impact.
    const effectiveScore = relevantEvaluations.length > 0 ? averageScore : 8;

    return {
      ...q,
      averageScore,
      evaluationCount: relevantEvaluations.length,
      effectiveScore
    };
  });
};

/**
 * Calculates Impact Score for recommendations.
 * Impact Score = Sum of (10 - QuestionScore) for all linked questions.
 * @param {Array} recommendations - List of recommendations
 * @param {Array} questionsWithScores - Questions with .effectiveScore
 * @param {Array} relations - Question-Recommendation relations
 * @returns {Object} Map of recommendationId -> { score, affectedCount }
 */
export const calculateImpactScores = (recommendations, questionsWithScores, relations) => {
  const impactMap = {};

  recommendations.forEach(rec => {
    // Find all questions linked to this rec
    // Use loose equality (==) for robust ID matching
    const linkedRelations = relations.filter(r => 
      r.recommendation_id == rec.id || r.recommendation == rec.id
    );
    
    // Get unique question IDs
    const linkedQuestionIds = [...new Set(linkedRelations.map(r => r.question_id))];
    
    let totalImpact = 0;
    let affectedCount = 0;

    linkedQuestionIds.forEach(qId => {
      // Use loose equality (==) to find the question
      const q = questionsWithScores.find(qs => qs.id == qId);
      if (q) {
        const gap = Math.max(0, 10 - q.effectiveScore);
        totalImpact += gap;
        affectedCount++;
      }
    });

    impactMap[rec.id] = {
      score: totalImpact,
      affectedCount
    };
  });

  return impactMap;
};